import { BuildOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import service from '@/services/index';

import { FilterItemWrapper, LabelRow, LabelText } from './styles';

type Props = {
  alias: string;
};

const TaxonomyCard = (props: Props) => {
  const columns = useStructuredFilterMap();
  const { parseStructuredFilter } = useStructuredFilter();
  const { data, isFetched } = useQuery({
    queryFn: () => {
      return service.taxonomy.fetchTaxonomyItem(props.alias);
    },
    queryKey: [...queryKeys.taxonomy.TAXONOMY_ITEM, props.alias],
  });

  const [filterArr, setFilterArr] = useState<StructuredFilterQueryFormatT[]>(
    [],
  );

  useEffect(() => {
    const filter = parseStructuredFilter(data?.filter || {});
    setFilterArr(filter);
  }, [data?.filter, parseStructuredFilter]);

  return (
    <Style>
      <Transition
        status={isFetched}
        fallback={
          <CenterFlex style={{ width: '100%', height: '100%' }}>
            <Spinner />
          </CenterFlex>
        }
      >
        {data && (
          <VFlex g={24}>
            <Flex a="center" g={8}>
              <BuildOutlined style={{ fontSize: 16 }} />
              <Typo type="body0" weight="sb">
                {data.title}
              </Typo>
            </Flex>
            <VFlex g={12}>
              <LabelRow>
                <LabelText>Description</LabelText>
                <Flex padding="4px 0px">
                  <Typo
                    type="label3"
                    weight="m"
                    className={data.desc ? 'text-gray-700' : 'text-gray-500'}
                  >
                    {data.desc || 'No description'}
                  </Typo>
                </Flex>
              </LabelRow>
              <LabelRow>
                <LabelText>Feedback</LabelText>
                <ErrorBoundary
                  fallbackRender={() => {
                    return <div>Error occurred</div>;
                  }}
                >
                  <Flex g={4} f={1}>
                    {filterArr.map((filter) => {
                      const result = structuredFilterUtils.getFieldInfo(
                        filter.columnKey,
                        columns,
                      );
                      const fieldInfo = result?.fieldInfo;
                      if (!fieldInfo) return null;
                      return (
                        <FilterItemWrapper key={filter.columnKey}>
                          <Typo type="label3">{filter.columnKey}</Typo>
                          <Typo type="label3" className="text-gray-500">
                            {structuredFilterUtils.getOperatorTextMap({
                              operator: filter.operator,
                              hasMultiValues: filter.filterValues.length > 1,
                              isMultiValueField: fieldInfo.isMultiValueField,
                            })}
                          </Typo>
                          <Typo type="label3">
                            {filter.filterValues
                              .map((value) => value.filterName)
                              .join(', ')}
                          </Typo>
                        </FilterItemWrapper>
                      );
                    })}
                  </Flex>
                </ErrorBoundary>
              </LabelRow>
            </VFlex>
          </VFlex>
        )}
      </Transition>
    </Style>
  );
};

const Style = styled(Flex)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${(props) => props.theme.radius.card}px;
  padding: 24px;
`;

export default TaxonomyCard;
